/**
 * Pure grammar for the `:` command palette (REV14 — docs/specs/REV14-command-palette.md; extended
 * REV16 — docs/specs/REV16-clearboss-and-toastify.md — with the `clearboss` command). No React, no
 * Fuse construction in here: character (and, for `clearboss`, planned-boss) fuzzy-matching is
 * supplied by the caller (`PaletteContext.searchCharacters`/`searchPlannedBosses`) so this module
 * stays framework-agnostic and trivially unit-testable with stub search functions. Boss plan/clear
 * data likewise arrives pre-fetched via `PaletteContext.plannedBosses` — this module never touches
 * `fetch`/TanStack Query. `CommandPalette.tsx` is the only caller and owns the real Fuse instances
 * (screenshotImport's idiom: `keys: [...], threshold: 0.35, ignoreLocation: true`).
 *
 * Grammar:
 *   - Bare page words/aliases (`PAGE_ALIASES`) -> a `Pages` suggestion for the matching
 *     `NAV_ITEMS` entry (which stays the single source of truth for label/path).
 *   - `boss`/`pitch`/`char`/`clearboss` alone (no trailing space yet) also surface a `Commands`
 *     template suggestion alongside any `Pages` match — e.g. "pi" matches both the Pitched page
 *     and the `pitch <character>` template.
 *   - `boss <query>` / `pitch <query>` / `char <query>` (command word + at least one space)
 *     switches entirely to fuzzy character completion (`Characters` group).
 *   - `clearboss <charname> <bossname> [players] [nosell]` (REV16): a two-stage argument grammar.
 *     `<charname>` (the first whitespace-delimited token after "clearboss ") is resolved via an
 *     *exact* case-insensitive name match — not fuzzy — because the transition into the boss stage
 *     has to be deterministic. Until that token is followed by a space, suggestions stay in the
 *     `Characters` group (fuzzy, same shape as `boss <query>`'s rows, so Enter still does something
 *     useful: it jumps to that character's boss tab). Once resolved, the rest of the input is
 *     `<bossname> [players] [nosell]`: a trailing `nosell` token (case-insensitive) and, after that,
 *     a trailing all-digit token are stripped from the end (in that fixed order) before whatever
 *     remains is fuzzy-matched against that character's PLANNED bosses only (`Bosses` group) — a
 *     boss can only be cleared with a plan, same invariant as the Bosses page.
 */
import { NAV_ITEMS } from '../app/navigation';
import type { BossTierValue } from './bosses';

export type SuggestionGroup = 'Pages' | 'Commands' | 'Characters' | 'Bosses';

export type SuggestionAction =
  | { kind: 'navigate'; to: string; search?: string }
  | {
      kind: 'clearBoss';
      accountId: number;
      characterId: number;
      bossSlug: string;
      bossName: string;
      tier: BossTierValue;
      sharedClearGroup: string;
      difficulty: string;
      /** Present only when `[players]` was typed — already clamped to `1..min(partyMax ?? 6, 6)`. */
      partySize?: number;
      crystalSold: boolean;
    };

export interface Suggestion {
  id: string;
  label: string;
  sublabel?: string;
  group: SuggestionGroup;
  action: SuggestionAction;
  /** `Characters`-group rows only: enough for the component to render the avatar circle
   *  (`avatarProxyPath`) without a second roster lookup. Not part of the grammar itself. */
  avatarUrl?: string | null;
  accountId?: number;
  characterId?: number;
}

export interface PaletteCharacter {
  characterId: number;
  accountId: number;
  accountLabel: string;
  name: string;
  classSlug: string;
  /** REV18 (docs/specs/REV18-switch-classnames-toast-theme.md): the class catalog's friendly
   *  display name for `classSlug` (e.g. `"Dark Knight"`, not `"dark-knight"`), resolved by the
   *  caller (`CommandPalette.tsx`, which has `useClasses()`) — this module stays framework-
   *  agnostic and never touches the reference-data query itself. Falls back to the raw slug when
   *  the catalog hasn't loaded yet; see `lib/reference.ts`'s `classDisplayName`. */
  classDisplayName: string;
  level: number;
  avatarUrl: string | null;
}

/** A `clearboss` boss-stage candidate — one of the resolved character's `boss_plan` rows, trimmed
 *  to what the grammar needs (the component derives this from `bossMatrixQuery`'s cached plan +
 *  clear data; see `CommandPalette.tsx`). */
export interface PaletteBossPlan {
  bossSlug: string;
  bossName: string;
  tier: BossTierValue;
  sharedClearGroup: string;
  difficulty: string;
  /** `null` = uncapped party size for this difficulty (clamped to 6 regardless, same as the rest
   *  of the app — see `clampPartySize`). */
  partyMax: number | null;
  /** Whether this plan already has a clear recorded for the tier's CURRENT period (component-
   *  computed via `periodKeyForTier`). Purely informational — executing stays an idempotent
   *  upsert either way. */
  alreadyClearedThisPeriod: boolean;
}

export interface PaletteContext {
  /** The full roster, flattened — used verbatim when a command has no argument text yet. */
  characters: readonly PaletteCharacter[];
  /** Fuzzy character search over `characters`, supplied by the caller. */
  searchCharacters: (query: string) => PaletteCharacter[];
  /** `clearboss` boss stage only: the resolved character's planned bosses. Absent/empty while the
   *  component's lazy matrix fetch hasn't resolved yet. */
  plannedBosses?: readonly PaletteBossPlan[];
  /** Fuzzy search over `plannedBosses`, supplied by the caller (mirrors `searchCharacters`). */
  searchPlannedBosses?: (query: string) => PaletteBossPlan[];
}

type CommandWord = 'boss' | 'pitch' | 'char' | 'clearboss' | 'hexa';

/** Bare-word/alias -> page path. Several aliases may target the same page. */
const PAGE_ALIASES: Record<string, string> = {
  dash: '/',
  home: '/',
  today: '/',
  check: '/checklist',
  tasks: '/checklist',
  checklist: '/checklist',
  setup: '/checklist/manage',
  manage: '/checklist/manage',
  boss: '/bosses',
  bosses: '/bosses',
  pitch: '/pitched',
  pitched: '/pitched',
  chars: '/characters',
  characters: '/characters',
  links: '/links',
  legion: '/legion',
  hexa: '/hexa',
  settings: '/settings',
};

/**
 * REV20 (docs/specs/REV20-hexa-page.md) — `/settings` left `NAV_ITEMS` (replaced by the `UserMenu`
 * popover, freeing its mobile-tab-bar slot for `/hexa`). `buildPageSuggestions` below otherwise
 * only ever sources `Pages` rows from `NAV_ITEMS`, so without this the settings alias above would
 * still parse but never actually surface a suggestion to act on — this is the one `Pages` entry not
 * derived from `NAV_ITEMS`.
 */
const SETTINGS_PAGE_SUGGESTION = { label: 'Settings', to: '/settings' } as const;

interface CommandTemplate {
  word: CommandWord;
  label: string;
  sublabel: string;
  to: string;
}

const COMMAND_TEMPLATES: CommandTemplate[] = [
  { word: 'boss', label: 'boss <character>', sublabel: "Jump to a character's boss tab", to: '/bosses' },
  { word: 'pitch', label: 'pitch <character>', sublabel: "Jump to a character's Pitched tab", to: '/pitched' },
  { word: 'char', label: 'char <character>', sublabel: "Open a character's detail page", to: '/characters' },
  { word: 'hexa', label: 'hexa <character>', sublabel: "Jump to a character's HEXA tab", to: '/hexa' },
  {
    word: 'clearboss',
    label: 'clearboss <character> <boss> [players] [nosell]',
    sublabel: 'Clear a boss for the current period — crystal sold by default',
    to: '/bosses',
  },
];

/** Command word + at least one trailing space -> argument mode. Matched against the input with
 *  only *leading* whitespace stripped, so the trailing space that triggers argument mode (e.g.
 *  "boss ") is never lost to a naive full trim. */
const ARGUMENT_RE = /^(boss|pitch|char|hexa)\s+(.*)$/i;

/** Same idea as `ARGUMENT_RE` but for `clearboss`, which gets its own two-stage parser below rather
 *  than the single-fuzzy-argument handling the other three commands share. */
const CLEARBOSS_ARGUMENT_RE = /^clearboss\s+(.*)$/i;

/** Title-cases an all-caps enum value for display, e.g. `"HARD"` -> `"Hard"`, `"WEEKLY"` ->
 *  `"Weekly"`. Shared by the boss-stage sublabel here and the component's success-toast text. */
export function titleCase(value: string): string {
  return value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

/** `[players]`'s clamp rule: `1..min(plan.partyMax ?? 6, 6)`. */
function clampPartySize(playersRaw: number, partyMax: number | null): number {
  const cap = Math.min(partyMax ?? 6, 6);
  return Math.min(Math.max(playersRaw, 1), cap);
}

function characterSuggestion(command: CommandWord, character: PaletteCharacter): Suggestion {
  const base = {
    id: `${command}-character-${character.characterId}`,
    label: character.name,
    sublabel: `${character.classDisplayName} · Lv.${character.level} · ${character.accountLabel}`,
    group: 'Characters' as const,
    avatarUrl: character.avatarUrl,
    accountId: character.accountId,
    characterId: character.characterId,
  };
  if (command === 'char') {
    return { ...base, action: { kind: 'navigate', to: `/characters/${character.characterId}` } };
  }
  const to = command === 'pitch' ? '/pitched' : command === 'hexa' ? '/hexa' : '/bosses';
  return {
    ...base,
    action: {
      kind: 'navigate',
      to,
      search: `account=${character.accountId}&character=${character.characterId}`,
    },
  };
}

function buildPageSuggestions(normalized: string): Suggestion[] {
  const seenPaths = new Set<string>();
  const results: Suggestion[] = [];
  for (const item of NAV_ITEMS) {
    if (seenPaths.has(item.to)) continue;
    const labelMatch = normalized === '' || item.label.toLowerCase().startsWith(normalized);
    const aliasMatch =
      normalized !== '' &&
      Object.entries(PAGE_ALIASES).some(([alias, to]) => to === item.to && alias.startsWith(normalized));
    if (labelMatch || aliasMatch) {
      seenPaths.add(item.to);
      results.push({
        id: `page-${item.to}`,
        label: item.label,
        group: 'Pages',
        action: { kind: 'navigate', to: item.to },
      });
    }
  }

  // REV20: Settings is deliberately excluded from the empty-input "browse everything" list (it's
  // not in NAV_ITEMS any more, and shouldn't reappear as if it still had a tab-bar slot) — it only
  // surfaces once the user actually types toward it, same as any other alias/label prefix match.
  if (normalized !== '' && !seenPaths.has(SETTINGS_PAGE_SUGGESTION.to)) {
    const settingsLabelMatch = SETTINGS_PAGE_SUGGESTION.label.toLowerCase().startsWith(normalized);
    const settingsAliasMatch = Object.entries(PAGE_ALIASES).some(
      ([alias, to]) => to === SETTINGS_PAGE_SUGGESTION.to && alias.startsWith(normalized),
    );
    if (settingsLabelMatch || settingsAliasMatch) {
      results.push({
        id: `page-${SETTINGS_PAGE_SUGGESTION.to}`,
        label: SETTINGS_PAGE_SUGGESTION.label,
        group: 'Pages',
        action: { kind: 'navigate', to: SETTINGS_PAGE_SUGGESTION.to },
      });
    }
  }

  return results;
}

function buildCommandSuggestions(normalized: string): Suggestion[] {
  if (normalized === '') return [];
  return COMMAND_TEMPLATES.filter((t) => t.word.startsWith(normalized)).map((t) => ({
    id: `command-${t.word}`,
    label: t.label,
    sublabel: t.sublabel,
    group: 'Commands' as const,
    action: { kind: 'navigate' as const, to: t.to },
  }));
}

/** Splits the text after "clearboss " into its character token and whatever follows. `null` means
 *  the user hasn't typed a space past the first token yet — the character isn't committed, so we
 *  stay in fuzzy character-completion mode. */
function splitClearBossArgs(rest: string): { charToken: string; afterChar: string } | null {
  const firstSpaceIdx = rest.search(/\s/);
  if (firstSpaceIdx === -1) return null;
  return { charToken: rest.slice(0, firstSpaceIdx), afterChar: rest.slice(firstSpaceIdx + 1).trim() };
}

/** Strips the grammar's trailing optional tokens — `nosell` then an all-digit players count, in
 *  that fixed order — leaving whatever's left as the boss-name query. `exactOptionalPropertyTypes`
 *  means `playersRaw` must be omitted entirely when there's no players token, not set to
 *  `undefined`. */
function parseBossArgs(argText: string): { bossQuery: string; playersRaw?: number; nosell: boolean } {
  const tokens = argText.split(/\s+/).filter((t) => t.length > 0);
  let nosell = false;
  if (tokens.length > 0 && tokens[tokens.length - 1]!.toLowerCase() === 'nosell') {
    nosell = true;
    tokens.pop();
  }
  if (tokens.length > 0 && /^\d+$/.test(tokens[tokens.length - 1]!)) {
    const playersRaw = Number(tokens.pop());
    return { bossQuery: tokens.join(' '), playersRaw, nosell };
  }
  return { bossQuery: tokens.join(' '), nosell };
}

function clearBossCharacterSuggestion(character: PaletteCharacter): Suggestion {
  // Reuses `boss <character>`'s exact jump shape: if the palette closes before a boss is chosen,
  // Enter still does something useful. Re-namespaced id so it never collides with an actual
  // `boss <query>` suggestion list (never rendered at the same time, but ids should stay honest).
  return { ...characterSuggestion('boss', character), id: `clearboss-character-${character.characterId}` };
}

function bossStageSuggestion(
  character: PaletteCharacter,
  plan: PaletteBossPlan,
  playersRaw: number | undefined,
  nosell: boolean,
): Suggestion {
  // `exactOptionalPropertyTypes`: `partySize` must be omitted entirely, not set to `undefined`,
  // when `[players]` wasn't typed — hence the spread rather than a plain `partySize:` key.
  const partySize = playersRaw === undefined ? undefined : clampPartySize(playersRaw, plan.partyMax);
  return {
    id: `clearboss-boss-${character.characterId}-${plan.bossSlug}`,
    label: plan.bossName,
    sublabel:
      `${titleCase(plan.difficulty)} · ${titleCase(plan.tier)}` +
      (plan.alreadyClearedThisPeriod ? ' · already cleared' : ''),
    group: 'Bosses',
    action: {
      kind: 'clearBoss',
      accountId: character.accountId,
      characterId: character.characterId,
      bossSlug: plan.bossSlug,
      bossName: plan.bossName,
      tier: plan.tier,
      sharedClearGroup: plan.sharedClearGroup,
      difficulty: plan.difficulty,
      crystalSold: !nosell,
      ...(partySize !== undefined ? { partySize } : {}),
    },
  };
}

function buildClearBossSuggestions(rest: string, ctx: PaletteContext): Suggestion[] {
  const split = splitClearBossArgs(rest);
  if (!split) {
    const charQuery = rest.trim();
    const matches = charQuery === '' ? [...ctx.characters] : ctx.searchCharacters(charQuery);
    return matches.map(clearBossCharacterSuggestion);
  }
  const character = ctx.characters.find((c) => c.name.toLowerCase() === split.charToken.toLowerCase());
  if (!character) {
    // Moved past the character token (typed a space) but it doesn't exactly match anyone — fall
    // back to fuzzy suggestions for it so a typo is still recoverable.
    return ctx.searchCharacters(split.charToken).map(clearBossCharacterSuggestion);
  }
  const { bossQuery, playersRaw, nosell } = parseBossArgs(split.afterChar);
  const plans = ctx.plannedBosses ?? [];
  const matches = bossQuery === '' ? [...plans] : (ctx.searchPlannedBosses?.(bossQuery) ?? []);
  return matches.map((plan) => bossStageSuggestion(character, plan, playersRaw, nosell));
}

/** Resolves `clearboss <charname> ...`'s character token to a roster character via an *exact*
 *  case-insensitive name match, or `null` if the token isn't committed yet (no trailing space) or
 *  doesn't match anyone. Exported so the component can decide — without re-implementing this
 *  parsing — whether its lazy `bossMatrixQuery` should be enabled and for which account. */
export function resolveClearBossCharacter(
  input: string,
  characters: readonly PaletteCharacter[],
): PaletteCharacter | null {
  const withoutLeadingSpace = input.replace(/^\s+/, '');
  const match = CLEARBOSS_ARGUMENT_RE.exec(withoutLeadingSpace);
  if (!match) return null;
  const split = splitClearBossArgs(match[1] ?? '');
  if (!split) return null;
  return characters.find((c) => c.name.toLowerCase() === split.charToken.toLowerCase()) ?? null;
}

/**
 * Builds the palette's suggestion list for the current raw input. Pure — the same `input`/`ctx`
 * always yields the same output.
 */
export function buildSuggestions(input: string, ctx: PaletteContext): Suggestion[] {
  const withoutLeadingSpace = input.replace(/^\s+/, '');

  const clearBossMatch = CLEARBOSS_ARGUMENT_RE.exec(withoutLeadingSpace);
  if (clearBossMatch) {
    return buildClearBossSuggestions(clearBossMatch[1] ?? '', ctx);
  }

  const argMatch = ARGUMENT_RE.exec(withoutLeadingSpace);
  if (argMatch) {
    const command = argMatch[1]!.toLowerCase() as CommandWord;
    const query = argMatch[2]!.trim();
    const matches = query === '' ? [...ctx.characters] : ctx.searchCharacters(query);
    return matches.map((character) => characterSuggestion(command, character));
  }

  const normalized = input.trim().toLowerCase();
  return [...buildPageSuggestions(normalized), ...buildCommandSuggestions(normalized)];
}
